import type { ClearancePageFilter } from '@gis/clearance';
import { useCallback } from 'react';

import { GenerateReportDialog } from '@/components/GenerateReportDialog';
import { useFeature } from '@/hooks/useFeature';
import { useDialog } from '@/providers/DialogProvider';

/**
 * What an ANALYSIS OVERLAY PANEL asks for: a single, unscoped section for the
 * layer the panel is describing — no station window, no bin count.
 *
 * It carries no station fields at all, which is the point of keeping it as its
 * own type rather than making every field of `OpenAnalysisReportArgs` optional:
 * the dock's three station documents genuinely need a window and a bin count,
 * and softening them to `?` so a panel could omit them would delete that
 * requirement from the type for the dock as well.
 *
 * Split into two single-literal `mode` variants — rather than one interface
 * with `mode: 'deviation' | 'surfaceDifference'` — because the two kinds carry
 * different metadata (a deviation layer's has `deviationStats`, a
 * surface-difference layer's has `surfaceDifference.stats`) and asking for the wrong
 * section type fails server-side with a message naming the OTHER analysis
 * kind (the bug this distinction fixes). A union-typed discriminant also
 * defeats TypeScript's control-flow narrowing below — each variant needs its
 * own literal for `args.mode === 'deviation'` / `'surfaceDifference'` checks
 * to fully eliminate it from the union afterwards.
 */
export interface OpenDeviationReportArgs {
  projectId: string;
  layerId: string;
  layerName: string;
  /** No section — `GenerateReportDialog` defaults to `DEVIATION`. */
  mode: 'deviation';
}

/**
 * `SurfaceDifferencePanel`'s own request: the `SURFACE_DIFFERENCE` section —
 * that layer's metadata has no `deviationStats` to ask a `DEVIATION` section
 * for, and no `stationVolumes` unless the run had an alignment.
 */
export interface OpenSurfaceDifferenceReportArgs {
  projectId: string;
  layerId: string;
  layerName: string;
  mode: 'surfaceDifference';
}

/**
 * A paired compare-surfaces result's "Create report" (spec §4.3, §12.2,
 * ruling R27): the run's one combined PDF, the same document its own chained
 * report is, named by the analysis group. Either panel of the pair asks for
 * the same thing.
 */
export interface OpenComparisonReportArgs {
  projectId: string;
  groupId: string;
  /** The cut & fill layer; its name is the report's default title. */
  layerId: string;
  layerName: string;
  mode: 'comparison';
}

/**
 * What the station-volumes dock's Export menu asks for. One callback, three
 * documents — the mode travels with the request rather than being guessed
 * from the other fields.
 */
export interface OpenAnalysisReportArgs {
  projectId: string;
  layerId: string;
  layerName: string;
  /**
   * `summary` is the one-section station-volumes report; `perStation` is the
   * A4-landscape page-per-bin document (spec §13); `layerStack` is the
   * multi-layer THICKNESS COMPLIANCE report (spec §7) — the structure
   * summary, the compliance table and the roll-up verdict.
   */
  mode: 'summary' | 'perStation' | 'layerStack';
  /** Bins the request covers — pages, in `perStation` mode; the cross-layer
   *  station-bin total, in `layerStack` mode. */
  binCount: number;
  /** Bins in the same window, blank ones included: what the render job would
   *  draw, and what its time estimate is computed from. */
  renderCount: number;
  stationFrom: number | null;
  stationTo: number | null;
  stationRangeLabel: string | null;
  /** Whether this run can produce per-station point-cloud renders (spec §13.7). */
  rendersAvailable: boolean;
  /** Original deviation range, used as placeholders for export-only render overrides. */
  renderRange?: [number, number] | null;
  /** Original render colormap, used as the export colormap default. */
  renderColormap?: string | null;
  /** `perStation` on a layer stack: which of its N−1 layers to print. */
  stackLayerIndex?: number;
  /** `layerStack` only: N−1, for the dialog's scope line. */
  layerCount?: number;
  /** `perStation` only: how many section overlays (Task 14) are still
   *  `pending` — sampled server-side but not drawn yet, so the pages this
   *  job prints will be missing their reference line(s) (Task 17). */
  overlaysPendingCount?: number;
}

/**
 * The clearance dock's whole-run summary PDF (clearance spec §5.2). Always the
 * whole run: the brush windows only the per-station document.
 */
export interface OpenClearanceReportArgs {
  projectId: string;
  layerId: string;
  layerName: string;
  mode: 'clearance';
  /** The dock's own "alignment changed" verdict; the dialog repeats it. */
  alignmentStale?: boolean;
}

/**
 * The clearance dock's per-interval PDF: one page per scored bin in the
 * brushed window, or only the flagged (FAIL / MARGINAL) ones. The dock counts
 * both options so the dialog can quote them and disable one that cannot print.
 */
export interface OpenClearancePagesReportArgs {
  projectId: string;
  layerId: string;
  layerName: string;
  mode: 'clearancePages';
  /** Internal stations covering the brush, `null` for the whole run. */
  stationFrom: number | null;
  stationTo: number | null;
  stationRangeLabel: string | null;
  /** Scored bins in the window — the ALL option's page count. */
  allCount: number;
  /** Scored bins in the window that are not clear — the FLAGGED option's. */
  flaggedCount: number;
  defaultFilter: ClearancePageFilter;
  alignmentStale?: boolean;
}

/** Everything this handler can be asked for: the dock's three station
 *  documents, the two overlay panels' single-layer sections, a paired
 *  run's combined document, and the clearance dock's two documents. */
export type OpenAnalysisReportRequest =
  | OpenAnalysisReportArgs
  | OpenDeviationReportArgs
  | OpenSurfaceDifferenceReportArgs
  | OpenComparisonReportArgs
  | OpenClearanceReportArgs
  | OpenClearancePagesReportArgs;

/**
 * The one place a PDF report is opened from: the station-volumes dock's
 * "PDF report" items (rulings T10.6 / T15.6, and
 * spec §7 for the stack). The dock stays presentation-only; the dialog and
 * the paywall live here.
 *
 * This is a HOOK rather than a body inside `Viewer.tsx` for one reason: the
 * `LAYER_STACK` report was built, tested and reviewed with no caller, because
 * the only thing standing between the dock and the dialog was a closure
 * nothing could mount. A hook can be mounted, so
 * `app/src/pages/__tests__/layerStackReachable.dom.test.tsx` drives the whole
 * user path — dock item → this handler → the real dialog → the real mutation
 * payload — instead of asserting that a renderer renders.
 *
 * The overlay analysis panels (`DeviationVolumePanel` with `mode:
 * 'deviation'`, `SurfaceDifferencePanel` with `mode: 'surfaceDifference'`) use
 * it too, for exactly the same reason — a report handler written inline in a
 * panel is a closure nothing can mount, and the two panels had one each.
 *
 * `stationFrom` / `stationTo` are internal stations covering the dock's brush,
 * `null` for the whole corridor.
 */
export const useOpenAnalysisReport = (): ((args: OpenAnalysisReportRequest) => void) => {
  const { openDialog } = useDialog();
  const reportGate = useFeature('report.pdf');

  return useCallback(
    (args: OpenAnalysisReportRequest) => {
      // Soft paywall: the mutation is PLUS-only, so offer the upgrade rather
      // than letting the request fail on the server. It covers all three
      // modes — they are the same mutation and the same tier.
      if (!reportGate.allowed) {
        reportGate.promptUpgrade();
        return;
      }
      // `DeviationVolumePanel` wants the dialog's own default section, so it
      // passes no `section` at all rather than a `ReportSectionRequest` tag
      // that would mean the same thing.
      if (args.mode === 'deviation') {
        openDialog(
          <GenerateReportDialog projectId={args.projectId} layerId={args.layerId} layerName={args.layerName} />,
          'sm:max-w-[480px]',
        );
        return;
      }
      // `SurfaceDifferencePanel`'s layer has no `deviationStats`. Its own
      // section needs no alignment — asking for `STATION_VOLUMES` here failed
      // every run made without one — and the server appends the corridor
      // pages when the run has them.
      if (args.mode === 'surfaceDifference') {
        openDialog(
          <GenerateReportDialog
            projectId={args.projectId}
            layerId={args.layerId}
            layerName={args.layerName}
            section={{ type: 'SURFACE_DIFFERENCE' }}
          />,
          'sm:max-w-[480px]',
        );
        return;
      }
      if (args.mode === 'comparison') {
        openDialog(
          <GenerateReportDialog
            projectId={args.projectId}
            layerId={args.layerId}
            layerName={args.layerName}
            section={{ type: 'COMPARISON', groupId: args.groupId }}
          />,
          'sm:max-w-[480px]',
        );
        return;
      }
      if (args.mode === 'clearance') {
        openDialog(
          <GenerateReportDialog
            projectId={args.projectId}
            layerId={args.layerId}
            layerName={args.layerName}
            section={{ type: 'CLEARANCE', alignmentStale: args.alignmentStale ?? false }}
          />,
          'sm:max-w-[480px]',
        );
        return;
      }
      if (args.mode === 'clearancePages') {
        openDialog(
          <GenerateReportDialog
            projectId={args.projectId}
            layerId={args.layerId}
            layerName={args.layerName}
            section={{
              type: 'CLEARANCE_PAGES',
              stationFrom: args.stationFrom,
              stationTo: args.stationTo,
              allCount: args.allCount,
              flaggedCount: args.flaggedCount,
              defaultFilter: args.defaultFilter,
              alignmentStale: args.alignmentStale ?? false,
            }}
            stationRangeLabel={args.stationRangeLabel ?? undefined}
          />,
          'sm:max-w-[480px]',
        );
        return;
      }
      openDialog(
        <GenerateReportDialog
          projectId={args.projectId}
          layerId={args.layerId}
          layerName={args.layerName}
          section={
            args.mode === 'layerStack'
              ? {
                  type: 'LAYER_STACK',
                  stationFrom: args.stationFrom,
                  stationTo: args.stationTo,
                  layerCount: args.layerCount ?? 0,
                  binCount: args.binCount,
                }
              : args.mode === 'perStation'
                ? {
                    type: 'STATION_PAGES',
                    stationFrom: args.stationFrom,
                    stationTo: args.stationTo,
                    binCount: args.binCount,
                    renderCount: args.renderCount,
                    rendersAvailable: args.rendersAvailable,
                    renderRange: args.renderRange ?? null,
                    renderColormap: args.renderColormap ?? null,
                    ...(typeof args.stackLayerIndex === 'number' ? { stackLayerIndex: args.stackLayerIndex } : {}),
                    overlaysPendingCount: args.overlaysPendingCount ?? 0,
                  }
                : {
                    type: 'STATION_VOLUMES',
                    stationFrom: args.stationFrom,
                    stationTo: args.stationTo,
                  }
          }
          stationRangeLabel={args.stationRangeLabel ?? undefined}
        />,
        'sm:max-w-[480px]',
      );
    },
    [openDialog, reportGate],
  );
};
